// TODO add tests https://calmdotcom.atlassian.net/browse/EVE-9069
import { RbcReason, UsercryptMultiselect } from '@calm/evexia-web-sdk';
import { UsercryptChoiceIcon } from '@rhg/components/usercrypt-choice-icon';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useFormattedConditions } from '@rhg/hooks/use-formatted-conditions';
import { useLatestCheckinEntries } from '@rhg/hooks/use-latest-checkin-entries';
import { useScreeningResults } from '@rhg/hooks/use-screening-results';
import { CheckinResultItem } from '@rhg/hooks/use-screening-results/types';
import {
    ConditionsOutline,
    DailySpark,
    ProfileOutlineAvatar,
} from '@rhg/libs/material/icons';
import { CheckinTypeKey } from '@rhg/utils/usercrypt/checkins';
import { useMemo } from 'react';
import { useGenders } from '../use-genders';
import { usePartnerDisplayLogic } from '../use-partner-display-logic';
import { useIsRegisteredAndSponsored } from '../use-sponsorship-eligibility';
import {
    GetMatchingChoicesPodData,
    MatchingChoicesPodData,
    MatchingPod,
} from './types';

interface LearnWhyDialogDataResponse {
    anxietyPodData: CheckinResultItem | undefined;
    depressionPodData: CheckinResultItem | undefined;
    conditionsPodData: MatchingChoicesPodData | undefined;
    topicsPodData: MatchingChoicesPodData | undefined;
    goalsPodData: MatchingChoicesPodData | undefined;
    ageGenderPodData: MatchingChoicesPodData | undefined;
    age: string | undefined;
    gender: string | undefined;
    isLoading: boolean;
}

const getMentalHealthPodData = (
    checkinResult: CheckinResultItem | undefined,
    reasonScore: number | undefined
): CheckinResultItem | undefined => {
    if (typeof reasonScore !== 'number' || !checkinResult) {
        return undefined;
    }

    return checkinResult;
};

const getAgeGenderMatchingPodData = (
    age?: string,
    gender?: string,
    genderDisplayName?: string
): GetMatchingChoicesPodData | undefined => {
    if (age && gender) {
        return {
            matchingChoices: [age, gender] as string[],
            pod: MatchingPod.AgeGender,
            totalCount: 2,
        };
    } else if (age) {
        return { matchingChoices: [age], pod: MatchingPod.Age, totalCount: 1 };
    } else if (gender) {
        return {
            matchingChoices: [gender],
            // Fake metadata to get proper display name
            choiceMetadata: {
                maxSelectOption: 1,
                choices: [
                    {
                        id: 'gender',
                        displayName: genderDisplayName,
                        name: gender,
                    },
                ],
            },
            pod: MatchingPod.Gender,
            totalCount: 1,
        };
    }
    return undefined;
};

const getMatchingChoicesPodIcon = (
    choice: string,
    pod: MatchingPod,
    choiceMetadata?: UsercryptMultiselect
) => {
    switch (pod) {
        case MatchingPod.Goals: {
            const choiceImage = choiceMetadata?.choices.find(
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                (c) => c.name === choice
            )?.image;
            return <UsercryptChoiceIcon image={choiceImage} />;
        }
        case MatchingPod.Topics:
            return <DailySpark />;
        case MatchingPod.Conditions:
            return <ConditionsOutline />;
        case MatchingPod.AgeGender:
        case MatchingPod.Age:
        case MatchingPod.Gender:
            return <ProfileOutlineAvatar />;
    }
};

const getMatchingChoiceDisplayNameAndCategory = (
    choice: string,
    choiceMetadata?: UsercryptMultiselect
) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const match = choiceMetadata?.choices.find((c) => c.name === choice);
    return {
        displayName: match?.displayName ?? choice,
        category: match?.category,
    };
};

const getMatchingChoicesPodData = ({
    matchingChoices,
    totalCount,
    pod,
    choiceMetadata,
}: GetMatchingChoicesPodData): MatchingChoicesPodData | undefined => {
    if (!matchingChoices || typeof totalCount === 'undefined') {
        return undefined;
    }

    return {
        totalCount,
        matchingChoices: matchingChoices.map((choice) => {
            const displayNameAndCategory =
                getMatchingChoiceDisplayNameAndCategory(choice, choiceMetadata);

            return {
                label: choice,
                icon: getMatchingChoicesPodIcon(choice, pod, choiceMetadata),
                ...displayNameAndCategory,
            };
        }),
    };
};

export const useLearnWhyDialogData = (
    reason?: RbcReason
): LearnWhyDialogDataResponse => {
    const { genders, isLoading: isGendersLoading } = useGenders();
    const { activeCircleId } = useActiveCircle();

    const {
        latestResult: _latestScreeningResult,
        isFetched: isScreeningResultsFetched,
    } = useScreeningResults();
    const { latestCheckinEntries, isLoading: isLatestEntriesLoading } =
        useLatestCheckinEntries([CheckinTypeKey.Goal, CheckinTypeKey.Topic]);

    const latestGoals = latestCheckinEntries[CheckinTypeKey.Goal];
    const latestTopics = latestCheckinEntries[CheckinTypeKey.Topic];
    const { addedConditions, isLoading: isConditionsLoading } =
        useFormattedConditions(activeCircleId);

    const { showScreeningResults, isLoading: isLoadingDisplayLogic } =
        usePartnerDisplayLogic();
    const {
        isRegistered,
        isSponsored,
        isLoading: isRegCheckLoading,
    } = useIsRegisteredAndSponsored();
    const bypassResults = isSponsored && isRegistered && !showScreeningResults;

    const isLoading = useMemo(
        () =>
            !isScreeningResultsFetched ||
            isGendersLoading ||
            isLatestEntriesLoading ||
            isConditionsLoading ||
            isLoadingDisplayLogic ||
            isRegCheckLoading,
        [
            isConditionsLoading,
            isGendersLoading,
            isLatestEntriesLoading,
            isLoadingDisplayLogic,
            isRegCheckLoading,
            isScreeningResultsFetched,
        ]
    );

    const latestScreeningResult =
        isRegCheckLoading || isLoadingDisplayLogic || bypassResults
            ? undefined
            : _latestScreeningResult;

    const anxietyPodData = useMemo(
        () =>
            getMentalHealthPodData(
                latestScreeningResult?.gad,
                reason?.gadScore
            ),
        [latestScreeningResult?.gad, reason?.gadScore]
    );

    const depressionPodData = useMemo(
        () =>
            getMentalHealthPodData(
                latestScreeningResult?.phq,
                reason?.phqScore
            ),
        [latestScreeningResult?.phq, reason?.phqScore]
    );

    const ageGenderPodData = useMemo(() => {
        const ageGenderMatchingPodData = getAgeGenderMatchingPodData(
            reason?.age,
            reason?.gender,
            genders.find(({ key }) => key === reason?.gender)?.displayName
        );

        if (!ageGenderMatchingPodData) {
            return undefined;
        }

        return getMatchingChoicesPodData(ageGenderMatchingPodData);
    }, [genders, reason?.age, reason?.gender]);

    const goalsPodData = useMemo(
        () =>
            getMatchingChoicesPodData({
                matchingChoices: reason?.goals,
                totalCount: latestGoals?.latestEntry?.checkins.values.length,
                pod: MatchingPod.Goals,
                choiceMetadata:
                    latestGoals?.meta.requirements?.primary?.multiselect,
            }),
        [
            reason?.goals,
            latestGoals?.latestEntry?.checkins.values.length,
            latestGoals?.meta.requirements?.primary?.multiselect,
        ]
    );

    const topicsPodData = useMemo(
        () =>
            getMatchingChoicesPodData({
                matchingChoices: reason?.topics,
                totalCount: latestTopics?.latestEntry?.checkins.values.length,
                pod: MatchingPod.Topics,
                choiceMetadata:
                    latestTopics?.meta.requirements?.primary?.multiselect,
            }),
        [
            reason?.topics,
            latestTopics?.latestEntry?.checkins.values.length,
            latestTopics?.meta.requirements?.primary?.multiselect,
        ]
    );

    const conditionsPodData = useMemo(
        () =>
            getMatchingChoicesPodData({
                matchingChoices: reason?.conditions,
                totalCount: addedConditions.length,
                pod: MatchingPod.Conditions,
            }),
        [reason?.conditions, addedConditions.length]
    );

    return {
        anxietyPodData,
        depressionPodData,
        goalsPodData,
        topicsPodData,
        conditionsPodData,
        ageGenderPodData,
        age: reason?.age,
        gender: reason?.gender,
        isLoading,
    };
};
